import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { useSelector } from 'react-redux';
import { Typography } from '@components/Typography';
import ActionIconButton from '@components/ActionIconButton';
import { Card, CardFooter, CardControls, CardIconButton, CardDivider } from '@components/CardItem/styles';
import CardOwnerLabel from '@components/CardItem/OwnerLabel';
import { OWNER_TYPE } from '@components/OwnerIcon';
import { useTranslation, useLocations } from '@hooks';
import StatusDot from '@components/StatusDot';

const MicrositeCard = ({ microsite, isSubmitting, onClickDelete, onClickEdit, onClickView, canWrite }) => {
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const brands = useSelector(state => state.brands.data);

    const internalName = microsite.brandId
        ? brands?.find(b => b.id === microsite.brandId)?.title
        : locationsMap[microsite.locationId]?.internalName;

    const hasActiveDomain = microsite.customDomainHostname && microsite.customDomainStatus === 1;
    const micrositeUrl = hasActiveDomain
        ? `https://${microsite.customDomainHostname}/${microsite.slug}`
        : `${import.meta.env.REACT_APP_SURVEY_DOMAIN}/${microsite.slug}`;

    return (
        <Card sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2">{microsite.name}</Typography>
            <CardOwnerLabel type={microsite.brandId ? OWNER_TYPE.BRAND : OWNER_TYPE.LOCATION} name={internalName} />
            <div style={{ marginTop: 'auto' }} />
            <CardDivider />
            <CardFooter>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, overflow: 'hidden' }}>
                    {microsite.customDomainHostname && (
                        <Typography
                            variant="caption"
                            noWrap
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                maxWidth: '100%',
                            }}
                        >
                            <StatusDot status={microsite.customDomainStatus ?? 0} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {microsite.customDomainHostname}
                            </span>
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {t('MicrositesPage.visits')}{' '}
                        <span style={{ color: 'var(--text-primary)' }}>{microsite.visitorsCount || '0'}</span>
                    </Typography>
                </div>
                <CardControls columns={canWrite ? 3 : 1}>
                    {canWrite ? (
                        <>
                            <ActionIconButton
                                title={t('buttons.open')}
                                component={CardIconButton}
                                href={micrositeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                disabled={isSubmitting}
                            >
                                <LinkIcon />
                            </ActionIconButton>
                            <ActionIconButton
                                title={t('common.edit')}
                                component={CardIconButton}
                                disabled={isSubmitting}
                                onClick={onClickEdit}
                            >
                                <EditIcon />
                            </ActionIconButton>
                            <ActionIconButton
                                title={t('common.delete')}
                                component={CardIconButton}
                                disabled={isSubmitting}
                                onClick={onClickDelete}
                            >
                                <DeleteIcon />
                            </ActionIconButton>
                        </>
                    ) : (
                        <ActionIconButton title={t('common.view')} component={CardIconButton} onClick={onClickView}>
                            <VisibilityIcon />
                        </ActionIconButton>
                    )}
                </CardControls>
            </CardFooter>
        </Card>
    );
};

export default MicrositeCard;
